import { useSearchParams } from 'react-router-dom';
import { ProductCard, SeoHead } from '@shared/components';
import { PaginationComponent } from '@shared/components/ui/Pagination';
import { useProductSearchResults } from '@shared/hooks/useProductSearch';
import { ProductSkeleton } from '@shared/components/skeletons';
import { generateCollectionPageSchema } from '@shared/utils/schemaOrg';
import type { ProductSearchResult } from '@shared/hooks/useProductSearch';

export function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 12; // Productos por página

  // Hook para obtener resultados de búsqueda
  const {
    data: searchData,
    isLoading, 
    error
  } = useProductSearchResults(query, currentPage, pageSize);


  // Manejar cambio de página
  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Datos de paginación
  const paginationMeta = searchData?.meta;
  const totalPages = paginationMeta?.totalPages ?? 1;
  const totalItems = paginationMeta?.total ?? 0;
  const activePage = paginationMeta?.page ?? currentPage;

  // Productos de la búsqueda (cada resultado tiene { product, relevance })
  const products = (searchData?.data && Array.isArray(searchData.data)) 
    ? searchData.data.map((result: ProductSearchResult) => result.product)
    : [];

  // SEO dinámico según búsqueda
  const searchTitle = query 
    ? `Resultados de búsqueda: "${query}" | Cerámicas Gassull`
    : "Búsqueda de productos | Cerámicas Gassull";
  const searchDescription = query && totalItems > 0
    ? `Encontramos ${totalItems} resultado${totalItems !== 1 ? 's' : ''} para "${query}" en Cerámicas Gassull. Productos de construcción de alta calidad en Mendoza.`
    : "Busca productos de construcción, cerámicas y materiales en Cerámicas Gassull. Amplio catálogo disponible en Mendoza, Argentina.";
  const canonicalUrl = query 
    ? `/search?q=${encodeURIComponent(query)}${currentPage > 1 ? `&page=${currentPage}` : ''}`
    : "/search";
  
  const collectionSchema = generateCollectionPageSchema(
    searchTitle,
    searchDescription,
    canonicalUrl,
    products
  );

  return (
    <>
      <SeoHead
        title={searchTitle}
        description={searchDescription}
        canonical={canonicalUrl}
        noindex={!query || totalItems === 0} // No indexar si no hay búsqueda o resultados
        schemaJsonLd={collectionSchema}
      />
      <div className="min-h-screen">
      {/* Header de la página - Consistente con Products y Contact */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Resultados de búsqueda
            </h1>
            {query && (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {totalItems > 0 ? (
                  <>
                    {totalItems} resultado{totalItems !== 1 ? 's' : ''} para "<strong className="text-gray-900">{query}</strong>"
                  </>
                ) : isLoading ? (
                  <>Buscando resultados para "<strong className="text-gray-900">{query}</strong>"</>
                ) : (
                  <>No se encontraron resultados para "<strong className="text-gray-900">{query}</strong>"</>
                )}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Contenido principal */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 auto-rows-fr">
              {Array.from({ length: 8 }).map((_, index) => (
                <ProductSkeleton key={index} />
              ))}
            </div>
          ) : error ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="text-red-600 mb-4">
                <h2 className="text-2xl font-bold mb-2">
                  Error en la búsqueda
                </h2>
                <p className="text-gray-600 mb-6">
                  No se pudieron cargar los resultados para "{query}"
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-3 bg-[#282828] text-white rounded-lg font-medium hover:bg-[#1a1a1a] transition-colors"
                >
                  Reintentar
                </button>
              </div>
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="text-gray-400 mb-6">
                <svg
                  className="w-20 h-20 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                No se encontraron productos
              </h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                No hay resultados para "<strong className="text-gray-900">{query}</strong>". 
                Intenta con otros términos de búsqueda.
              </p>
              <div className="space-y-2 text-sm text-gray-500 max-w-sm mx-auto">
                <p>• Verifica la ortografía</p>
                <p>• Usa términos más generales</p>
                <p>• Intenta con sinónimos</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Grid de productos - Consistente con Products */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 auto-rows-fr">
                {products.map((product) => (
                  <div key={product.id} className="w-full">
                    <ProductCard
                      product={product}
                      variant="modern"
                      showQuantitySelector={false}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>

              {/* Controles de paginación - Consistente con Products */}
              {totalPages > 1 && (
                <PaginationComponent
                  currentPage={activePage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          )}
        </div>
      </section>
    </div>
    </>
  );
}
